import { SelfContainedCommand } from '../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../types/standardToolResults';
import { entityService } from '../../../services/entityService';

/**
 * Result for a single PDF passage match
 */
export interface PdfPassageResult {
  pdfId: string;
  pdfName: string;
  pageIndex: number;        // 0-based page index
  startIndex: number;       // Character start index within page
  endIndex: number;         // Character end index within page
  text: string;             // The matched text snippet
  score: number;            // Relevance score (0-100)
  context?: string;         // Optional surrounding context
}

/**
 * Find character indices of a substring within page text
 * 
 * @param pageText Full text of the page
 * @param snippet The text snippet to find
 * @returns Character indices or null if not found
 */
function findTextIndices(
  pageText: string,
  snippet: string
): { startIndex: number; endIndex: number } | null {
  // Normalize both texts (trim whitespace, collapse multiple spaces)
  const normalizeText = (text: string) => text.trim().replace(/\s+/g, ' ');
  
  const normalizedPage = normalizeText(pageText);
  const normalizedSnippet = normalizeText(snippet);
  
  const startIndex = normalizedPage.indexOf(normalizedSnippet);
  
  if (startIndex === -1) {
    // Try fuzzy matching - find the best match within page
    // For now, return approximate indices based on word position
    const words = normalizedPage.split(' ');
    const snippetWords = normalizedSnippet.split(' ');
    
    if (snippetWords.length === 0) return null;
    
    // Find first word of snippet in page
    const firstWord = snippetWords[0];
    let charPos = 0;
    for (let i = 0; i < words.length; i++) {
      if (words[i].toLowerCase().includes(firstWord.toLowerCase())) {
        // Found approximate location
        return {
          startIndex: charPos,
          endIndex: Math.min(charPos + snippet.length, normalizedPage.length - 1)
        };
      }
      charPos += words[i].length + 1; // +1 for space
    }
    
    return null;
  }
  
  return {
    startIndex,
    endIndex: startIndex + normalizedSnippet.length - 1
  };
}

/**
 * Calculate keyword relevance score
 * 
 * @param query Search query
 * @param text Text to score against
 * @returns Score from 0-100
 */
function calculateRelevance(query: string, text: string): number {
  const queryWords = query.toLowerCase().split(/\s+/).filter(w => w.length > 2);
  const textLower = text.toLowerCase();
  
  console.log('[calculateRelevance] 🎯 Calculating relevance:', {
    query,
    queryWords,
    queryWordCount: queryWords.length,
    textLength: text.length
  });
  
  if (queryWords.length === 0) {
    console.log('[calculateRelevance] ⚠️ No valid query words (all < 3 chars)');
    return 0;
  }
  
  let matchCount = 0;
  let totalOccurrences = 0;
  const wordMatches: Record<string, number> = {};
  
  for (const word of queryWords) {
    const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(escaped, 'gi');
    const matches = textLower.match(regex);
    if (matches) {
      matchCount++;
      totalOccurrences += matches.length;
      wordMatches[word] = matches.length;
    } else {
      wordMatches[word] = 0;
    }
  }
  
  // Score based on:
  // - Percentage of query words found (70% weight)
  // - Number of occurrences (30% weight, capped)
  const wordMatchRatio = matchCount / queryWords.length;
  const occurrenceBonus = Math.min(totalOccurrences / 10, 1); // Max 10 occurrences
  
  const finalScore = Math.round((wordMatchRatio * 70) + (occurrenceBonus * 30));
  
  console.log('[calculateRelevance] 📊 Relevance calculated:', {
    matchCount,
    totalWords: queryWords.length,
    wordMatchRatio: (wordMatchRatio * 100).toFixed(1) + '%',
    totalOccurrences,
    occurrenceBonus: (occurrenceBonus * 100).toFixed(1) + '%',
    finalScore,
    wordMatches
  });
  
  return finalScore;
}

/**
 * Extract best snippet from text around query keywords
 * 
 * @param text Full page text
 * @param query Search query
 * @param maxLength Max snippet length
 * @returns Best matching snippet
 */
function extractBestSnippet(text: string, query: string, maxLength: number = 300): string {
  const queryWords = query.toLowerCase().split(/\s+/).filter(w => w.length > 2);
  
  if (queryWords.length === 0 || text.length <= maxLength) {
    return text.substring(0, maxLength);
  }
  
  // Find first occurrence of any query word
  let bestPos = -1;
  let bestWord = '';
  
  for (const word of queryWords) {
    const pos = text.toLowerCase().indexOf(word);
    if (pos !== -1 && (bestPos === -1 || pos < bestPos)) {
      bestPos = pos;
      bestWord = word;
    }
  }
  
  if (bestPos === -1) {
    // No query words found, return start of text
    return text.substring(0, maxLength);
  }
  
  // Center snippet around the match
  const halfLength = Math.floor(maxLength / 2);
  let start = Math.max(0, bestPos - halfLength);
  let end = Math.min(text.length, start + maxLength);
  
  // Adjust start if we're at the end
  if (end === text.length) {
    start = Math.max(0, end - maxLength);
  }
  
  // Try to break at word boundaries
  if (start > 0) {
    const spaceAfter = text.indexOf(' ', start);
    if (spaceAfter !== -1 && spaceAfter < start + 20) {
      start = spaceAfter + 1;
    }
  }
  
  if (end < text.length) {
    const spaceBefore = text.lastIndexOf(' ', end);
    if (spaceBefore !== -1 && spaceBefore > end - 20) {
      end = spaceBefore;
    }
  }
  
  let snippet = text.substring(start, end);
  if (start > 0) snippet = '...' + snippet;
  if (end < text.length) snippet = snippet + '...';
  
  return snippet;
}

/**
 * Search for text content within PDF documents
 * 
 * Returns passages with exact character ranges that can be highlighted and navigated to.
 * 
 * Example AI usage:
 *   AI: "Let me search your PDFs for evidence about moderate alcohol consumption"
 *   Tool: search_pdf_content(query="moderate alcohol consumption protective effects")
 *   Result: [...passages with exact page/character locations...]
 */
export const searchPdfContentCommand: SelfContainedCommand = {
  id: 'search_pdf_content',
  description: `Search for text content within PDF documents.

**SUPPORTED ENTITIES:**
- PDFs (pdf_* or file_*)
- For Documents/Notes/Sheets/Canvas/Slides, use appropriate read tools instead

Returns passages with exact locations (page, character indices) for highlighting in PDF viewer. Use to find evidence, locate topics, or answer questions using PDF content.`,
  
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',
  
  schema: {
    parameters: [
      {
        name: 'query',
        type: 'string',
        description: 'Search query - keywords, phrases, or concepts to find',
        required: true
      },
      {
        name: 'pdfIds',
        type: 'array',
        description: 'Optional array of specific PDF entity IDs to search. If omitted, searches all PDFs in the library.',
        required: false,
        validator: (value: any) => {
          if (!value) return null; // Optional parameter
          if (!Array.isArray(value)) return 'pdfIds must be an array';
          // Check each ID in the array
          for (const id of value) {
            if (typeof id !== 'string') return 'pdfIds must be an array of strings';
            if (id.startsWith('doc_') || id.startsWith('note_') || id.startsWith('sheet_')) {
              return 'search_pdf_content is for PDFs only (pdf_* or file_*). Cannot search Documents, Notes, or Sheets.';
            }
          }
          return null;
        }
      },
      {
        name: 'limit',
        type: 'number',
        description: 'Maximum number of results to return (default: 10)',
        required: false
      },
      {
        name: 'minScore',
        type: 'number',
        description: 'Minimum relevance score (0-100) to include (default: 30)',
        required: false
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { 
          query: 'moderate alcohol consumption cardiovascular health',
          limit: 5
        },
        expectedOutput: {
          status: 'success',
          message: 'Found 5 passages across 3 PDFs',
          format: 'json',
          data: { 
            query: 'moderate alcohol consumption cardiovascular health',
            results: [],
            totalResults: 5,
            pdfsSearched: 3
          }
        },
        description: 'Search all PDFs for passages about alcohol and heart health'
      },
      {
        input: { 
          query: 'machine learning neural networks',
          pdfIds: ['pdf-123', 'pdf-456'],
          minScore: 50
        },
        expectedOutput: {
          status: 'success',
          message: 'Found 3 passages across 2 PDFs',
          format: 'json',
          data: { results: [], totalResults: 3 }
        },
        description: 'Search specific PDFs with higher relevance threshold'
      }
    ],
    bad: [
      {
        input: { query: '' },
        error: 'Empty search query',
        why: 'Empty query',
        fix: 'Provide a search query with keywords or phrases'
      }
    ]
  },
  
  handler: async (params, context): Promise<StandardToolResult> => {
    console.log('[search_pdf_content] 🔍 SEARCH STARTED:', {
      query: params.query,
      pdfIds: params.pdfIds,
      limit: params.limit || 10,
      minScore: params.minScore || 30
    });
    
    try {
      const { 
        query, 
        pdfIds = undefined, 
        limit = 10, 
        minScore = 30 
      } = params;
      
      if (!query || query.trim().length === 0) {
        console.error('[search_pdf_content] ❌ Empty query provided');
        return createErrorResult(
          'Empty search query',
          'Please provide a search query',
          'validation'
        );
      }
      
      console.log('[search_pdf_content] 📋 Validated params:', { query, limit, minScore });
      
      const { spaceId } = context;
      console.log('[search_pdf_content] 🏠 Space context:', { spaceId });
      
      // Get PDFs to search
      let pdfsToSearch;
      
      if (pdfIds && Array.isArray(pdfIds) && pdfIds.length > 0) {
        console.log('[search_pdf_content] 🎯 Searching specific PDFs:', pdfIds);
        // Search specific PDFs
        pdfsToSearch = await Promise.all(
          pdfIds.map(id => entityService.get(id))
        );
        console.log('[search_pdf_content] ✅ Loaded specific PDFs:', {
          count: pdfsToSearch.length,
          names: pdfsToSearch.map(p => p.name)
        });
      } else {
        console.log('[search_pdf_content] 🌍 Searching all PDFs in space');
        // Search all PDFs in space
        const allEntities = await entityService.queryEntities({
          spaceId
        });
        console.log('[search_pdf_content] 📦 Got entities from space:', {
          total: allEntities.length,
          types: Array.from(new Set(allEntities.map(e => e.entityType)))
        });
        
        // Filter for PDF entities
        pdfsToSearch = allEntities.filter(entity => 
          entity.entityType?.includes('PDF') ||
          entity.skeleton?.fileType === 'PDF' ||
          entity.skeleton?.mimeType === 'application/pdf'
        );
        console.log('[search_pdf_content] 📄 Filtered to PDFs:', {
          count: pdfsToSearch.length,
          names: pdfsToSearch.map(p => p.name)
        });
      }
      
      if (pdfsToSearch.length === 0) {
        console.warn('[search_pdf_content] ⚠️ No PDFs found to search');
        return createSuccessResult(
          'No PDFs found to search',
          {
            query,
            results: [],
            totalResults: 0,
            pdfsSearched: 0
          }
        );
      }
      
      console.log('[search_pdf_content] 🚀 Starting search across PDFs:', pdfsToSearch.length);
      
      // Search each PDF
      const allResults: PdfPassageResult[] = [];
      
      for (const pdf of pdfsToSearch) {
        console.log('[search_pdf_content] 📖 Searching PDF:', {
          id: pdf._id,
          name: pdf.name,
          hasEnrichments: !!pdf.skeleton?.enrichments,
          hasEnrichedData: !!pdf.skeleton?.enrichedData,
          enrichedDataKeys: pdf.skeleton?.enrichedData ? Object.keys(pdf.skeleton.enrichedData) : []
        });
        
        // Try multiple paths for enrichment data
        // Path 1: skeleton.enrichments.sections (PDFiumApp format)
        // Path 2: skeleton.enrichedData.sections (backend format)
        const sections = pdf.skeleton?.enrichments?.sections || pdf.skeleton?.enrichedData?.sections;
        
        if (!sections) {
          console.warn('[search_pdf_content] ⏭️ Skipping (no sections found):', {
            pdfName: pdf.name,
            hasEnrichments: !!pdf.skeleton?.enrichments,
            hasEnrichedData: !!pdf.skeleton?.enrichedData,
            enrichmentStatus: pdf.skeleton?.enrichmentStatus
          });
          continue;
        }
        
        console.log('[search_pdf_content] ✅ Found sections:', {
          pdfName: pdf.name,
          sectionCount: Object.keys(sections).length,
          sectionKeys: Object.keys(sections)
        });
        
        for (const [pageKey, pageContent] of Object.entries(sections)) {
          // Extract page number from "Page X" format
          const pageMatch = pageKey.match(/Page (\d+)/);
          if (!pageMatch) {
            console.warn('[search_pdf_content] ⚠️ Invalid page key format:', pageKey);
            continue;
          }
          
          const pageNumber = parseInt(pageMatch[1], 10);
          const pageIndex = pageNumber - 1; // Convert to 0-based
          
          // Get page text (handle both string and object formats)
          let pageText: string;
          if (typeof pageContent === 'string') {
            pageText = pageContent;
          } else if (typeof pageContent === 'object' && pageContent && 'text' in pageContent) {
            pageText = (pageContent as any).text;
          } else {
            console.warn('[search_pdf_content] ⚠️ Invalid page content type:', {
              page: pageKey,
              type: typeof pageContent
            });
            continue;
          }
          
          if (!pageText || pageText.trim().length === 0) {
            console.warn('[search_pdf_content] ⚠️ Empty page text:', pageKey);
            continue;
          }
          
          console.log('[search_pdf_content] 🔍 Searching page:', {
            pdf: pdf.name,
            page: pageKey,
            textLength: pageText.length,
            textPreview: pageText.substring(0, 100)
          });
          
          // Calculate relevance
          const relevance = calculateRelevance(query, pageText);
          
          console.log('[search_pdf_content] 📊 Relevance score:', {
            pdf: pdf.name,
            page: pageKey,
            score: relevance,
            threshold: minScore,
            passed: relevance >= minScore
          });
          
          if (relevance < minScore) {
            continue;
          }
          
          // Extract best snippet
          const snippet = extractBestSnippet(pageText, query, 300);
          console.log('[search_pdf_content] ✂️ Extracted snippet:', {
            pdf: pdf.name,
            page: pageKey,
            snippetLength: snippet.length,
            snippet: snippet
          });
          
          // Find character indices for the snippet
          const indices = findTextIndices(pageText, snippet);
          console.log('[search_pdf_content] 📍 Character indices:', {
            pdf: pdf.name,
            page: pageKey,
            found: !!indices,
            indices
          });
          
          if (!indices) {
            // Fallback: use start of page
            allResults.push({
              pdfId: pdf._id,
              pdfName: pdf.name || 'Unnamed PDF',
              pageIndex,
              startIndex: 0,
              endIndex: Math.min(300, pageText.length - 1),
              text: snippet,
              score: relevance
            });
          } else {
            allResults.push({
              pdfId: pdf._id,
              pdfName: pdf.name || 'Unnamed PDF',
              pageIndex,
              startIndex: indices.startIndex,
              endIndex: indices.endIndex,
              text: snippet,
              score: relevance
            });
          }
          
          console.log('[search_pdf_content] ➕ Added result:', {
            pdf: pdf.name,
            page: pageKey,
            score: relevance,
            totalResultsSoFar: allResults.length
          });
        }
      }
      
      console.log('[search_pdf_content] 📊 Search complete, sorting results:', {
        totalResults: allResults.length,
        scores: allResults.map(r => ({ pdf: r.pdfName, page: r.pageIndex, score: r.score }))
      });
      
      // Sort by relevance score (descending)
      allResults.sort((a, b) => b.score - a.score);
      
      console.log('[search_pdf_content] 🏆 Top results after sorting:', {
        count: Math.min(allResults.length, limit),
        top3: allResults.slice(0, 3).map(r => ({
          pdf: r.pdfName,
          page: r.pageIndex,
          score: r.score,
          snippet: r.text.substring(0, 50)
        }))
      });
      
      // Limit results
      const topResults = allResults.slice(0, limit);
      
      // Count unique PDFs
      const uniquePdfIds = new Set(topResults.map(r => r.pdfId));
      
      console.log('[search_pdf_content] ✅ SEARCH COMPLETE:', {
        query,
        totalResults: topResults.length,
        pdfsSearched: pdfsToSearch.length,
        pdfsWithResults: uniquePdfIds.size,
        resultScores: topResults.map(r => r.score)
      });
      
      return createSuccessResult(
        `Found ${topResults.length} passage${topResults.length !== 1 ? 's' : ''} across ${uniquePdfIds.size} PDF${uniquePdfIds.size !== 1 ? 's' : ''}`,
        {
          query,
          results: topResults,
          totalResults: topResults.length,
          pdfsSearched: pdfsToSearch.length,
          pdfsWithResults: uniquePdfIds.size
        }
      );
      
    } catch (error) {
      console.error('[search_pdf_content] ❌ SEARCH FAILED:', {
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      return createErrorResult(
        'Failed to search PDF content',
        error instanceof Error ? error.message : 'Unknown error',
        'execution'
      );
    }
  }
};

